import { useCallback, useEffect, useMemo, useRef } from 'react'
import { toast } from 'react-toastify'

import { Card, type WCCard } from '@loadsmart/miranda-react'

import { useAnalytics } from 'core/analytics/useAnalytics'

import { TrackingMethod, TrackingProvider, TT_PROVIDER_LINK } from '../../../../domain/Tracking'
import { useTrackingOnboardingContext } from '../TrackingOnboardingContext'
import { APIFinalizeConnectionContent } from './components/APIFinalizeConnectionContent'
import { EDIFinalizeConnectionContent } from './components/EDIFinalizeConnectionContent'
import { ELDFinalizeConnectionContent } from './components/ELDFinalizeConnectionContent'
import { ManualFinalizeConnectionContent } from './components/ManualFinalizeConnectionContent'
import { MobileFinalizeConnectionContent } from './components/MobileFinalizeConnectionContent'
import {
  formatFinalizeConnectionButtonText,
  formatFinalizeConnectionStepSubtitle,
  formatFinalizeConnectionStepTitle,
} from './presenters/finalizeConnectionStepPresenter'

export type FinalizeConnectionStepProps = {
  readonly selectedMethod?: TrackingMethod
  readonly selectedProvider?: TrackingProvider
  readonly onConfirmed: () => void
}

function openProviderWebsite(
  hasPreviousTracking: boolean,
  method?: TrackingMethod,
  provider?: TrackingProvider
) {
  if (hasPreviousTracking) return
  if (method !== TrackingMethod.ELD && method !== TrackingMethod.API) return

  if (provider === TrackingProvider.TT) {
    window.open(TT_PROVIDER_LINK, '_blank', 'noopener')
  }

  if (provider === TrackingProvider.MACROPOINT) {
    window.open('https://macropoint-lite.com/Connect.aspx?MPID=5580941', '_blank', 'noopener')
  }
}

function showContent(selectedMethod?: TrackingMethod, selectedProvider?: TrackingProvider) {
  if (
    selectedMethod === TrackingMethod.EDI ||
    selectedMethod === TrackingMethod.Manual ||
    selectedMethod === TrackingMethod.API
  ) {
    return true
  }

  return Boolean(selectedProvider && selectedMethod)
}

export default function FinalizeConnectionStep({
  selectedMethod,
  selectedProvider,
  onConfirmed,
}: FinalizeConnectionStepProps) {
  const { track } = useAnalytics()
  const cardRef = useRef<WCCard>(null)

  const {
    isPendingUpdate,
    isDoneUpdate,
    hasErrorUpdate,
    hasPreviousTracking,
    updatePreferredTrackingMethod,
  } = useTrackingOnboardingContext()

  const handleOnConfirm = useCallback(() => {
    updatePreferredTrackingMethod({ method: selectedMethod, provider: selectedProvider })
    track('Saved: Tracking setup drawer', {
      method: selectedMethod,
      provider: selectedProvider,
    })
  }, [selectedMethod, selectedProvider, track, updatePreferredTrackingMethod])

  const cardContent = useMemo(() => {
    switch (selectedMethod) {
      case TrackingMethod.API:
        return <APIFinalizeConnectionContent selectedProvider={selectedProvider} />
      case TrackingMethod.EDI:
        return <EDIFinalizeConnectionContent />
      case TrackingMethod.Manual:
        return <ManualFinalizeConnectionContent />
      case TrackingMethod.Mobile:
        return <MobileFinalizeConnectionContent selectedProvider={selectedProvider} />
      case TrackingMethod.ELD:
        return <ELDFinalizeConnectionContent selectedProvider={selectedProvider} />
    }
  }, [selectedMethod, selectedProvider])

  const showCardContent = showContent(selectedMethod, selectedProvider)

  useEffect(() => {
    if (isDoneUpdate) {
      toast.success('Preferred tracking connection confirmed.')
      onConfirmed()
      openProviderWebsite(hasPreviousTracking, selectedMethod, selectedProvider)
    }
  })

  return (
    <Card collapsible={showCardContent} ref={cardRef}>
      <Card.Title>{formatFinalizeConnectionStepTitle(hasPreviousTracking)}</Card.Title>
      {showCardContent && (
        <>
          <Card.Subtitle>{formatFinalizeConnectionStepSubtitle(hasPreviousTracking)}</Card.Subtitle>
          <Card.Divider />
          <Card.Body>{cardContent}</Card.Body>
          <Card.Footer>
            <Card.ActionPrimary
              onClick={handleOnConfirm}
              loading={isPendingUpdate}
              variant={hasErrorUpdate ? 'danger' : 'primary'}
            >
              {formatFinalizeConnectionButtonText({
                method: selectedMethod,
                provider: selectedProvider,
                hasErrorUpdate,
                hasPreviousTracking,
              })}
            </Card.ActionPrimary>
          </Card.Footer>
        </>
      )}
    </Card>
  )
}
